/**
 * What UI/UX adds around the board's own drawing: a frame's marks (◇ for a component, the start
 * flag in prototype mode before its title, its device and size after it), prototype mode's
 * button picture, and the Play button at the end of the tool column (`#board-play`).
 */
import { Show, type JSX } from "solid-js";
import { useApp, usePack } from "../../app/context";
import type { BoardFrame } from "../../state/board";
import { DEVICES } from "./constants";
import { startOf } from "./screens";
import type { UiUx } from "./service";

/** Before a frame's title: a component's mark, and in prototype mode the flag of the screen played first. */
export function FrameBefore(props: { frame: BoardFrame }): JSX.Element {
  const uiux = usePack<UiUx>("uiux");
  return (
    <>
      <Show when={props.frame.group.style?.component === "yes"}>
        <span class="board-frame-mark" title="A component: its instances follow it">
          ◇
        </span>
      </Show>
      <Show when={uiux.prototyping() && startOf(uiux.screens())?.id === props.frame.id}>
        <span class="board-start" id="board-start-flag" title="The prototype starts here">
          ▶ Start
        </span>
      </Show>
    </>
  );
}

/** After a frame's title: its device and size. */
export function FrameAfter(props: { frame: BoardFrame }): JSX.Element {
  return (
    <Show when={DEVICES[props.frame.group.style?.device ?? ""]}>
      {(d) => (
        <span class="board-frame-device">
          {d().label} {props.frame.place.w}×{props.frame.place.h}
        </span>
      )}
    </Show>
  );
}

/** Prototype mode's button picture: a way leading on. */
export function PrototypeIcon(): JSX.Element {
  return (
    <svg viewBox="0 0 16 16" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round" aria-hidden="true">
      <circle cx="4" cy="4" r="2" />
      <path d="M5.5 5.5C8 8 9 10 13 12M13 12h-3.5M13 12V8.5" />
    </svg>
  );
}

/** The Play button, at the end of the board's tool column. */
export function PlayButton(): JSX.Element {
  const { commands, keys } = useApp();
  const shortcut = () => keys.shortcut("prototype.play");
  return (
    <button type="button" id="board-play" title={shortcut() ? `Play the prototype (${shortcut()})` : "Play the prototype"} aria-label="Play the prototype" onClick={() => commands.run("prototype.play")}>
      <svg viewBox="0 0 16 16" width="18" height="18" fill="currentColor" aria-hidden="true">
        <path d="M4.5 2.5v11l9-5.5z" />
      </svg>
    </button>
  );
}
